import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TemplateChips = ({ domainId, onPick, isManager }) => {
  const [templates, setTemplates] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const [newText, setNewText] = useState('');

  const load = useCallback(() => {
    axios.get(`${API}/sms/reply-templates`, { params: domainId ? { domain_id: domainId } : {} })
      .then((r) => setTemplates(r.data.templates || [])).catch(() => setTemplates([]));
  }, [domainId]);
  useEffect(() => { load(); }, [load]);

  if (!templates) return null;

  const saveEdit = async () => {
    try {
      await axios.patch(`${API}/sms/reply-templates/${editId}`, { content: editText.trim() });
      toast.success('Template updated');
      setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const addNew = async () => {
    if (!newText.trim()) return;
    try {
      await axios.post(`${API}/sms/reply-templates`, { content: newText.trim(), domain_id: domainId });
      toast.success('Template added');
      setNewText(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };
  const remove = async (id) => {
    try {
      await axios.delete(`${API}/sms/reply-templates/${id}`);
      toast.success('Template deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="mb-1.5" data-testid="template-chips">
      <div className="flex items-center gap-1.5 flex-wrap">
        {templates.map((t) => (
          <button key={t.id} onClick={() => onPick(t)}
            className="text-[11px] font-semibold px-2 py-1 rounded-full bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
            data-testid={`template-chip-${t.id}`} title="Tap to use this reply">
            {t.content.length > 40 ? `${t.content.slice(0, 40)}…` : t.content}
          </button>
        ))}
        {isManager && (
          <button onClick={() => setEditing(!editing)}
            className="text-[11px] px-2 py-1 rounded-full text-amber-600 hover:text-amber-900 inline-flex items-center gap-1"
            data-testid="template-edit-toggle">
            <Pencil className="w-3 h-3" /> {editing ? 'Done' : 'Edit templates'}
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-2 space-y-1.5 bg-white border border-amber-200 rounded-lg p-2.5" data-testid="template-editor">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center gap-2" data-testid={`template-row-${t.id}`}>
              {editId === t.id ? (
                <>
                  <input value={editText} onChange={(e) => setEditText(e.target.value.slice(0, 459))}
                    className="flex-1 text-xs text-slate-900 bg-white border border-amber-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
                    autoFocus data-testid={`template-edit-input-${t.id}`} />
                  <button onClick={saveEdit} className="text-emerald-600 hover:text-emerald-800" data-testid={`template-save-${t.id}`}><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                </>
              ) : (
                <>
                  <p className="flex-1 text-xs text-slate-700">{t.content}</p>
                  <button onClick={() => { setEditId(t.id); setEditText(t.content); }}
                    className="text-slate-400 hover:text-amber-700" data-testid={`template-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(t.id)}
                    className="text-slate-400 hover:text-red-600" data-testid={`template-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </>
              )}
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1 border-t border-amber-100">
            <input value={newText} onChange={(e) => setNewText(e.target.value.slice(0, 459))}
              placeholder="New template…"
              className="flex-1 text-xs text-slate-900 bg-white border border-amber-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
              data-testid="template-new-input" />
            <button onClick={addNew} disabled={!newText.trim()}
              className="text-xs font-semibold px-2 py-1.5 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-white inline-flex items-center gap-1"
              data-testid="template-add-btn">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
